import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useAxiosPrivate from '../../context/hooks/useAxiosPrivate';
import { Button, Form, ButtonGroup } from 'react-bootstrap';
import '../../appstyles/TrackingCSS/TrackingDocuments.css';

const DocumentList = () => {
  const axiosInstance = useAxiosPrivate();
  const { trackId } = useParams();
  const [tracking, setTracking] = useState(null); 
  const [fileNames, setFileNames] = useState({}); 
  const [files, setFiles] = useState({});


  const documentFields = useMemo(() => [
    "contract_file", "bl_file", "invoice_file", "packing_list_file",
    "coa_file", "insurance_file", "origin_certificate_file",
    "sanitary_certificate_file", "phytosanitary_certificate_file"
  ], []);

  const documentTypes = [
    { id: 'contract_file', label: 'Contrato' },
    { id: 'bl_file', label: 'Bill of Landing o Guía Aérea' },
    { id: 'invoice_file', label: 'Factura' },
    { id: 'packing_list_file', label: 'Lista de empaque' },
    { id: 'coa_file', label: 'COA' },
    { id: 'insurance_file', label: 'Seguro' },
    { id: 'origin_certificate_file', label: 'Certificado de origen' },
    { id: 'sanitary_certificate_file', label: 'Certificado sanitario' },
    { id: 'phytosanitary_certificate_file', label: 'Certificado fitosanitario' }
  ];


  // Fetch file name
  const fetchFileName = useCallback(async (docType, fileId) => {
    try {
      const response = await axiosInstance.get(`api/logistics-tracking/file-name/?file_id=${fileId}`);
      setFileNames((prev) => ({ ...prev, [docType]: response.data.file_name }));
    } catch (error) {
      console.error(`Failed to fetch file name for ${docType}`, error);
    }
  }, [axiosInstance]);

  // Fetch tracking details
  useEffect(() => {
    const fetchTrackingData = async () => {
      try {
        const response = await axiosInstance.get(`api/logistics-tracking/${trackId}/`);
        setTracking(response.data);
        documentFields.forEach((docType) => {
          const fileId = response.data[docType];
          if (fileId) {
            fetchFileName(docType, fileId);
            setFiles((prev) => ({ ...prev, [docType]: fileId }));
          }
        });
      } catch (error) {
        console.error('Failed to fetch tracking details', error);
      }
    };
    if (trackId) {
      fetchTrackingData();
    }
  }, [trackId, axiosInstance, documentFields, fetchFileName]);



  // Handle file download
  const handleDownload = async (docType) => {
    try {
  // ...existing code...
      const fileId = tracking?.[docType];
      if (!fileId) return;

      const response = await axiosInstance.get(`api/logistics-tracking/download-file/?file_id=${fileId}`,
        { responseType: 'blob' }
      );
      if (response.status === 200) {
  // ...existing code...
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileNames[docType] || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Download failed');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Handle file deletion
  const handleDelete = async (docType) => {
    try {
  // ...existing code...
      const fileId = tracking?.[docType];
      if (!fileId) return;

      const response = await axiosInstance.post(`api/logistics-tracking/delete-file/?file_id=${fileId}`, {
      });

      if (response.status === 200) {
  // ...existing code...
        setFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[docType];
          return newFiles;
        });
      } else {
        console.error('Deletion failed');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  // Handle file upload
  const handleUpload = async (docType, file) => {
    try {
      const validDocType = documentTypes.find((type) => type.id === docType);
      if (!validDocType) {
        console.error(`Invalid document type: ${docType}`);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', validDocType.id);
      formData.append('tracking_id', trackId);

      const response = await axiosInstance.post('api/logistics-tracking/upload-file/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        window.location.reload();
      } else {
        console.error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  return (
    <div className="document-list-container">
      <h4 style={{fontWeight: '700'}}>Listado de Documentos</h4>
      <div className="document-list"> 
      {documentTypes.map((docType) => (
        <Form.Group key={docType.id} controlId={`formFile-${docType.id}`} className="mb-3">
          <Form.Label>{docType.label}</Form.Label>
          {files[docType.id] ? (
            <div style={{ fontStyle: 'italic', marginBottom: '8px', width: '70%', textAlign: 'center' }}>
              {fileNames[docType.id]}
            </div>
          ) : (
            <Form.Control
              type="file"
              onChange={(e) => e.target.files[0] && handleUpload(docType.id, e.target.files[0])}
              disabled={!!files[docType.id]} 
            />
          )}
          <ButtonGroup aria-label="Basic example">
          <Button 
            variant="outline-primary"
            onClick={() => handleDownload(docType.id)}
            disabled={!files[docType.id]}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-download" viewBox="0 0 16 16">
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
            </svg>
          </Button>
          <Button 
            variant="outline-primary"
            onClick={() => handleDelete(docType.id)}
            disabled={!files[docType.id]}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-trash" viewBox="0 0 16 16">
              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
              <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
            </svg>
          </Button>
          </ButtonGroup>
        </Form.Group>
        ))}
      </div>
    </div>
  );
};

export default DocumentList;